import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

const root = document.querySelector('[data-exhibition]');
const canvas = root.querySelector('[data-canvas]');
const ui = {
  entry: root.querySelector('[data-entry]'), enter: root.querySelector('[data-enter]'),
  progress: root.querySelector('[data-progress]'), progressValue: root.querySelector('[data-progress-value]'),
  loadingLabel: root.querySelector('[data-loading-label]'), hud: root.querySelector('[data-hud]'),
  location: root.querySelector('[data-location]'), hint: root.querySelector('[data-hint]'),
  rooms: root.querySelector('[data-room-navigation]'), views: root.querySelector('[data-view-navigation]'),
  viewCount: root.querySelector('[data-view-count]'), viewLabel: root.querySelector('[data-view-label]'),
  viewDots: root.querySelector('[data-view-dots]'), prev: root.querySelector('[data-view-prev]'),
  next: root.querySelector('[data-view-next]'), hall: root.querySelector('[data-hall]'),
  fullscreen: root.querySelector('[data-fullscreen]'), sound: root.querySelector('[data-sound]'),
  ambientAudio: document.querySelector('[data-ambient-audio]'), musicCredit: document.querySelector('[data-music-credit]'),
  curtain: root.querySelector('[data-curtain]'),
  panel: root.querySelector('[data-artwork-panel]'), panelClose: root.querySelector('[data-panel-close]'),
  artworkBrowse: root.querySelector('[data-artwork-browse]'), artworkPrev: root.querySelector('[data-artwork-prev]'),
  artworkNext: root.querySelector('[data-artwork-next]'), artworkPosition: root.querySelector('[data-artwork-position]'),
  reference: root.querySelector('[data-artwork-reference]'), title: root.querySelector('[data-artwork-title]'),
  technique: root.querySelector('[data-artwork-technique]'), support: root.querySelector('[data-artwork-support]'),
  dimensions: root.querySelector('[data-artwork-dimensions]'), link: root.querySelector('[data-artwork-link]'),
  huntProgress: root.querySelector('[data-hunt-progress]'), huntCount: root.querySelector('[data-hunt-count]'),
  huntDialog: root.querySelector('[data-hunt-dialog]'), huntTitle: root.querySelector('[data-hunt-title]'),
  huntMessage: root.querySelector('[data-hunt-message]'), huntKicker: root.querySelector('[data-hunt-kicker]'),
  huntClose: root.querySelector('[data-hunt-close]'), huntReset: root.querySelector('[data-hunt-reset]'),
  error: root.querySelector('[data-error]')
};

const lang = document.body.dataset.language === 'en' ? 'en' : 'fr';
const copy = lang === 'en'
  ? { hall: 'Main hall', room: 'Exhibition room', works: 'works', loading: 'Hanging the artworks', loadingModels: 'Loading the gallery elements', loadingImages: 'Preparing the artworks', ready: 'The gallery is ready', unknown: 'Not specified', discover: 'rooms to discover', roomProgress: 'Room', viewProgress: 'Viewpoint', of: 'of' }
  : { hall: 'Hall principal', room: 'Salle d’exposition', works: 'œuvres', loading: 'Accrochage des œuvres', loadingModels: 'Chargement des éléments de la galerie', loadingImages: 'Préparation des œuvres', ready: 'La galerie est prête', unknown: 'Non renseigné', discover: 'salles à découvrir', roomProgress: 'Salle', viewProgress: 'Point de vue', of: 'sur' };

let renderer;
let sceneTransitioning = false;

function fadeToScene(changeScene) {
  if (sceneTransitioning) return;
  sceneTransitioning = true;
  ui.curtain.classList.add('is-active');
  setTimeout(() => {
    changeScene();
    requestAnimationFrame(() => {
      warmSceneForNavigation();
      setTimeout(() => ui.curtain.classList.remove('is-active'), 80);
    });
    setTimeout(() => { sceneTransitioning = false; }, 760);
  }, 600);
}
let scene;
let camera;

function warmSceneForNavigation() {
  if (!renderer || !scene || !camera) return;
  const savedPosition = camera.position.clone();
  const savedQuaternion = camera.quaternion.clone();
  const savedUp = camera.up.clone();
  const directions = [
    new THREE.Vector3(0, 0, -1),
    new THREE.Vector3(1, 0, 0),
    new THREE.Vector3(0, 0, 1),
    new THREE.Vector3(-1, 0, 0)
  ];
  scene.updateMatrixWorld(true);
  directions.forEach(direction => {
    camera.lookAt(savedPosition.clone().add(direction));
    camera.updateMatrixWorld(true);
    renderer.render(scene, camera);
  });
  camera.position.copy(savedPosition);
  camera.quaternion.copy(savedQuaternion);
  camera.up.copy(savedUp);
  camera.updateMatrixWorld(true);
  renderer.render(scene, camera);
}
let secretVideoElements = [];
let exhibition;
let currentRoom = null;
let currentView = 0;
let currentArtworkPage = 0;
let views = [];
let interactive = [];
let roomRoot;
let kineticObjects = [];
let scrollingTextures = [];
let tween = null;
let lookYaw = 0;
let lookPitch = 0;
let pointerDown = null;
let dragged = false;
let selectedArtwork = null;
let artworkNavigationPending = false;
let exhibitionEntered = false;
let ambientEnabled = localStorage.getItem('exhibitionAmbientSound') !== 'off';
let audioFadeFrame = 0;
let artworkEffectTimer = 0;
let huntFound = [];
let secretRoom = null;
const rabbitImageNames = ['lapin-01.png', 'lapin-02.png', 'lapin-03.png', 'lapin-04.png'];
let rabbitSessionSeed = Number(sessionStorage.getItem('msieurcRabbitSeed'));
if (!rabbitSessionSeed) {
  rabbitSessionSeed = Math.floor(Math.random() * rabbitImageNames.length) + 1;
  sessionStorage.setItem('msieurcRabbitSeed', String(rabbitSessionSeed));
}

function rabbitImageUrl(itemId = 0) {
  const index = (Number(itemId) * 7 + rabbitSessionSeed) % rabbitImageNames.length;
  return new URL(`assets/rabbits/${rabbitImageNames[index]}`, window.location.href).href;
}

const artworkEffects = {
  hearts: {
    colors: ['#e1283f', '#b20f2a', '#f05262'],
    icon: '<svg viewBox="0 0 24 24"><path d="M12 21.1 10.55 19.8C5.4 15.25 2 12.15 2 8.35 2 5.25 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.08A6.02 6.02 0 0 1 16.5 3C19.58 3 22 5.25 22 8.35c0 3.8-3.4 6.9-8.55 11.46L12 21.1Z"/></svg>'
  },
  music: {
    colors: ['#202221', '#555b58', '#a92d36'],
    icon: '<svg viewBox="0 0 24 24"><path d="M19 3v12.55A4 4 0 1 1 17 12V7.2l-8 1.7v8.65A4 4 0 1 1 7 14V5.2L19 3Z"/></svg>'
  },
  stars: {
    colors: ['#d7b857', '#f0d985', '#9a7a25'],
    icon: '<svg viewBox="0 0 24 24"><path d="m12 2.4 2.95 5.98 6.6.96-4.78 4.66 1.13 6.58L12 17.48l-5.9 3.1L7.23 14 2.45 9.34l6.6-.96L12 2.4Z"/></svg>'
  },
  sparkles: {
    colors: ['#f4eee0', '#d6c8a5', '#ffffff'],
    icon: '<svg viewBox="0 0 24 24"><path d="M12 1.8c.55 5.6 3.1 8.15 8.7 8.7-5.6.55-8.15 3.1-8.7 8.7-.55-5.6-3.1-8.15-8.7-8.7 5.6-.55 8.15-3.1 8.7-8.7Zm7.1 13.6c.22 2.25 1.25 3.28 3.5 3.5-2.25.22-3.28 1.25-3.5 3.5-.22-2.25-1.25-3.28-3.5-3.5 2.25-.22 3.28-1.25 3.5-3.5Z"/></svg>'
  },
  flowers: {
    colors: ['#d84b5f', '#f09ca8', '#e9c75f'],
    icon: '<svg viewBox="0 0 24 24"><path d="M12 9.1C8.3 3.2 3.7 5.5 6.2 10.6.45 9.2-.95 14.2 4.75 15.15.8 19.6 5.2 22.45 9.1 17.95 9.5 24 14.5 24 14.9 17.95c3.9 4.5 8.3 1.65 4.35-2.8 5.7-.95 4.3-5.95-1.45-4.55C20.3 5.5 15.7 3.2 12 9.1Zm0 6.2a3.3 3.3 0 1 1 0-6.6 3.3 3.3 0 0 1 0 6.6Z"/></svg>'
  },
  confetti: {
    colors: ['#c9283e', '#e5bd48', '#315c50', '#f4f1e9'],
    icon: '<svg viewBox="0 0 24 24"><path d="M5 3h6v8H5V3Zm9 8h5v10h-5V11ZM3 15h7v5H3v-5Zm12-12h6v5h-6V3Z"/></svg>'
  }
};

function celebrateArtwork(artwork) {
  const effectName = String(artwork?.effect || 'none');
  const customEmoji = effectName.startsWith('emoji:') ? effectName.slice(6).trim() : '';
  const effect = customEmoji ? { emoji: customEmoji, colors: ['#ffffff'] } : artworkEffects[effectName];
  if (!effect) return;

  let layer = root.querySelector('[data-artwork-effect]');
  if (!layer) {
    layer = document.createElement('div');
    layer.className = 'artwork-effect-flight';
    layer.dataset.artworkEffect = '';
    layer.setAttribute('aria-hidden', 'true');
    root.appendChild(layer);
  }

  clearTimeout(artworkEffectTimer);
  layer.replaceChildren();
  layer.dataset.effect = effectName;
  const fragment = document.createDocumentFragment();

  for (let index = 0; index < 24; index += 1) {
    const particle = document.createElement('span');
    particle.className = 'artwork-effect-flight__particle';
    if (effect.emoji) {
      particle.classList.add('is-emoji');
      particle.textContent = effect.emoji;
    } else {
      particle.innerHTML = effect.icon;
    }
    particle.style.setProperty('--particle-x', `${42 + Math.random() * 16}%`);
    particle.style.setProperty('--particle-drift', `${-105 + Math.random() * 210}px`);
    particle.style.setProperty('--particle-size', `${14 + Math.random() * 31}px`);
    particle.style.setProperty('--particle-delay', `${Math.random() * .9}s`);
    particle.style.setProperty('--particle-duration', `${2.4 + Math.random() * 1.65}s`);
    particle.style.setProperty('--particle-opacity', `${.34 + Math.random() * .66}`);
    particle.style.setProperty('--particle-color', effect.colors[index % effect.colors.length]);
    fragment.appendChild(particle);
  }

  layer.appendChild(fragment);
  layer.classList.remove('is-flying');
  requestAnimationFrame(() => layer.classList.add('is-flying'));
  artworkEffectTimer = window.setTimeout(() => {
    layer.classList.remove('is-flying');
    layer.replaceChildren();
  }, 5200);
}

function updateSoundControl() {
  if (!ui.sound) return;
  const label = ambientEnabled ? window.EXHIBITION_COPY.soundOff : window.EXHIBITION_COPY.soundOn;
  ui.sound.classList.toggle('is-muted', !ambientEnabled);
  ui.sound.setAttribute('aria-pressed', ambientEnabled ? 'true' : 'false');
  ui.sound.setAttribute('aria-label', label);
  ui.sound.title = label;
}

function fadeAmbient(target, duration = 700, pauseAfter = false) {
  if (!ui.ambientAudio) return;
  cancelAnimationFrame(audioFadeFrame);
  const startVolume = ui.ambientAudio.volume;
  const startedAt = performance.now();
  const step = now => {
    const progress = Math.min(1, (now - startedAt) / duration);
    ui.ambientAudio.volume = Math.max(0, Math.min(1, startVolume + (target - startVolume) * progress));
    if (progress < 1) audioFadeFrame = requestAnimationFrame(step);
    else if (pauseAfter) ui.ambientAudio.pause();
  };
  audioFadeFrame = requestAnimationFrame(step);
}

async function startAmbient() {
  if (!ambientEnabled || !exhibitionEntered || !ui.ambientAudio) return;
  try {
    ui.ambientAudio.volume = 0;
    await ui.ambientAudio.play();
    fadeAmbient(.18, 1200);
  } catch (_) {
    ambientEnabled = false;
    localStorage.setItem('exhibitionAmbientSound', 'off');
    updateSoundControl();
  }
}

function stopAmbient() {
  if (!ui.ambientAudio || ui.ambientAudio.paused) return;
  fadeAmbient(0, 350, true);
}
let artworkReturnView = null;
let viewPromptTimer = null;
const raycaster = new THREE.Raycaster();
const pointer = new THREE.Vector2();
const textureLoader = new THREE.TextureLoader();
const gltfLoader = new GLTFLoader();
const modelCache = new Map();
const clock = new THREE.Clock();

const modelAssets = {
  plantSoft: 'potted_plant_02',
  plantSmall: 'potted_plant_04',
  ottoman: 'Ottoman_01',
  armchair: 'modern_arm_chair_01',
  bench: 'painted_wooden_bench',
  ceilingLight: 'mounted_fluorescent_lights',
  extinguisher: 'fire-extenguisher',
  pedestal: 'pedistal',
  secretDoor: 'gallery_doors/old_wooden_door_wbdjciudw_mid.glb'
};

function modelUrl(assetId) {
  if (assetId.endsWith('.glb')) return `./assets/models/${assetId}`;
  return `./assets/models/${assetId}/${assetId}.glb`;
}

function cloneModelScene(source) {
  const clone = source.clone(true);
  clone.traverse(object => {
    if (!object.isMesh) return;
    object.geometry = object.geometry.clone();
    const sourceMaterials = Array.isArray(object.material) ? object.material : [object.material];
    const clonedMaterials = sourceMaterials.map(sourceMaterial => {
      const clonedMaterial = sourceMaterial.clone();
      Object.keys(clonedMaterial).forEach(key => {
        if (clonedMaterial[key]?.isTexture) clonedMaterial[key] = clonedMaterial[key].clone();
      });
      return clonedMaterial;
    });
    object.material = Array.isArray(object.material) ? clonedMaterials : clonedMaterials[0];
    object.castShadow = renderer?.shadowMap.enabled || false;
    object.receiveShadow = true;
  });
  return clone;
}

function loadModel(assetId) {
  if (!modelCache.has(assetId)) {
    modelCache.set(assetId, new Promise((resolve, reject) => {
      const attempt = retries => {
        gltfLoader.load(modelUrl(assetId), gltf => resolve(gltf.scene), undefined, error => {
          if (retries > 0) window.setTimeout(() => attempt(retries - 1), 180);
          else reject(error);
        });
      };
      attempt(2);
    }));
  }
  return modelCache.get(assetId);
}

function placeModel(assetId, options = {}) {
  const parent = options.parent || roomRoot;
  const anchor = new THREE.Group();
  anchor.position.set(...(options.position || [0, 0, 0]));
  parent.add(anchor);

  loadModel(assetId).then(source => {
    if (!anchor.parent) return;
    const model = cloneModelScene(source);
    model.rotation.set(...(options.rotation || [0, 0, 0]));
    model.updateMatrixWorld(true);
    const initialBox = new THREE.Box3().setFromObject(model);
    const initialSize = initialBox.getSize(new THREE.Vector3());
    let scale = options.scale || 1;
    if (options.targetWidth && initialSize.x > 0) scale = options.targetWidth / initialSize.x;
    if (options.targetSpan && Math.max(initialSize.x, initialSize.z) > 0) {
      scale = options.targetSpan / Math.max(initialSize.x, initialSize.z);
    }
    if (options.targetHeight && initialSize.y > 0) scale = options.targetHeight / initialSize.y;
    if (options.targetDepth && initialSize.z > 0) scale = options.targetDepth / initialSize.z;
    const scaleAxes = options.scaleAxes || [1, 1, 1];
    model.scale.set(scale * scaleAxes[0], scale * scaleAxes[1], scale * scaleAxes[2]);
    if (options.castShadow === false) {
      model.traverse(object => {
        if (object.isMesh) object.castShadow = false;
      });
    }
    model.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    model.position.set(-center.x, -box.min.y, -center.z);
    anchor.add(model);
    if (options.onLoad) options.onLoad(anchor, model);
  }).catch(error => {
    console.warn(`Unable to load exhibition model: ${assetId}`, error);
    if (options.fallback) options.fallback(anchor);
  });
  return anchor;
}

function webglAvailable() {
  try {
    const test = document.createElement('canvas');
    return Boolean(window.WebGL2RenderingContext && test.getContext('webgl2'));
  } catch (_) { return false; }
}

function setProgress(value) {
  const percent = Math.max(0, Math.min(100, Math.round(value)));
  ui.progress.style.width = `${percent}%`;
  ui.progressValue.textContent = `${percent}%`;
}

async function runPreloadQueue(items, worker, concurrency, onProgress) {
  const queue = [...items];
  let completed = 0;
  const consume = async () => {
    while (queue.length) {
      const item = queue.shift();
      try { await worker(item); } catch (_) {}
      completed += 1;
      onProgress(completed, items.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, Math.max(1, items.length)) }, consume));
}

function preloadRealisticDoorAsset() {
  return new Promise(resolve => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    loadRealisticDoor(finish);
    window.setTimeout(finish, 15000);
  });
}

async function preloadExhibitionAssets() {
  ui.loadingLabel.textContent = copy.loadingModels;
  const models = [...new Set(Object.values(modelAssets))];
  await runPreloadQueue(
    [...models, '__gallery_doors__'],
    item => item === '__gallery_doors__' ? preloadRealisticDoorAsset() : loadModel(item),
    2,
    (done, total) => setProgress(20 + (done / total) * 34)
  );

  ui.loadingLabel.textContent = copy.loadingImages;
  const floorBase = './assets/textures/gallery-floor/';
  const fallbackImages = exhibition.secretMedia?.fallbackImages || [];
  const urls = [
    `${floorBase}floor_diff_1k.jpg`,
    `${floorBase}floor_normal_1k.jpg`,
    `${floorBase}floor_rough_1k.jpg`,
    ...rabbitImageNames.map(name => `./assets/rabbits/${name}`),
    ...fallbackImages.slice(0, 3),
    ...exhibition.rooms.flatMap(room => room.artworks.map(artwork => artwork.thumbnail))
  ];
  const uniqueUrls = [...new Set(urls.filter(Boolean))];
  await runPreloadQueue(
    uniqueUrls,
    async url => {
      const response = await fetch(new URL(url, window.location.href), { cache: 'force-cache', credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Asset HTTP ${response.status}`);
      await response.blob();
    },
    3,
    (done, total) => setProgress(54 + (done / total) * 44)
  );

  if (ui.ambientAudio) {
    ui.ambientAudio.preload = 'auto';
    ui.ambientAudio.load();
  }
}

function preloadHighResolutionImages() {
  const urls = [...new Set(exhibition.rooms.flatMap(room => room.artworks.map(artwork => artwork.image)).filter(Boolean))];
  runPreloadQueue(
    urls,
    async url => {
      const response = await fetch(new URL(url, window.location.href), { cache: 'force-cache', credentials: 'same-origin' });
      if (response.ok) await response.blob();
    },
    1,
    () => {}
  );
}

function setupRenderer() {
  renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, matchMedia('(max-width: 800px)').matches ? 1.25 : 1.6));
  renderer.setSize(innerWidth, innerHeight, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.shadowMap.enabled = !matchMedia('(max-width: 800px)').matches;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  scene = new THREE.Scene();
  scene.background = new THREE.Color(0x171918);
  scene.fog = new THREE.Fog(0x171918, 17, 37);
  camera = new THREE.PerspectiveCamera(matchMedia('(max-width: 800px)').matches ? 64 : 55, innerWidth / innerHeight, 0.1, 80);
  camera.position.set(0, 1.7, 8.8);
  scene.add(new THREE.HemisphereLight(0xdde1dc, 0x252927, 1.55));
  const key = new THREE.DirectionalLight(0xfff8ed, 2.4);
  key.position.set(-4, 8, 5);
  key.castShadow = renderer.shadowMap.enabled;
  scene.add(key);
}

function material(color, roughness = 0.82) {
  return new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.03 });
}

function addBox(parent, size, position, color, rotation = [0, 0, 0]) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), material(color));
  mesh.position.set(...position);
  mesh.rotation.set(...rotation);
  mesh.receiveShadow = true;
  parent.add(mesh);
  return mesh;
}

function addBasicBox(parent, size, position, color, rotation = [0, 0, 0]) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), new THREE.MeshBasicMaterial({ color }));
  mesh.position.set(...position);
  mesh.rotation.set(...rotation);
  parent.add(mesh);
  return mesh;
}

function surfaceTexture(type, baseColor, repeatX, repeatY) {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = baseColor;
  ctx.fillRect(0, 0, 512, 512);
  let seed = type === 'floor' ? 9187 : type === 'wall' ? 4319 : 7253;
  const random = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };
  const grainCount = type === 'floor' ? 7200 : 4200;
  for (let index = 0; index < grainCount; index += 1) {
    const shade = type === 'floor' ? (random() > .5 ? 255 : 20) : (random() > .5 ? 255 : 80);
    ctx.fillStyle = `rgba(${shade},${shade},${shade},${type === 'floor' ? .055 : .022})`;
    const size = type === 'floor' && random() > .86 ? 2 : 1;
    ctx.fillRect(random() * 512, random() * 512, size, size);
  }
  if (type === 'floor') {
    ctx.strokeStyle = 'rgba(255,255,255,.045)';
    ctx.lineWidth = 1;
    [128, 256, 384].forEach(value => {
      ctx.beginPath(); ctx.moveTo(value, 0); ctx.lineTo(value, 512); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, value); ctx.lineTo(512, value); ctx.stroke();
    });
  }
  if (type === 'ceiling') {
    ctx.strokeStyle = 'rgba(255,255,255,.025)';
    ctx.lineWidth = 2;
    for (let value = 0; value <= 512; value += 64) {
      ctx.beginPath(); ctx.moveTo(value, 0); ctx.lineTo(value, 512); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, value); ctx.lineTo(512, value); ctx.stroke();
    }
  }
  if (type === 'wood') {
    ctx.strokeStyle = 'rgba(74,51,29,.18)';
    ctx.lineWidth = 2;
    for (let line = 0; line < 34; line += 1) {
      const y = 8 + line * 15 + random() * 6;
      ctx.beginPath();
      for (let x = 0; x <= 512; x += 12) {
        const wave = Math.sin((x / 42) + line * .7) * (2 + random() * 1.5);
        if (x === 0) ctx.moveTo(x, y + wave); else ctx.lineTo(x, y + wave);
      }
      ctx.stroke();
    }
    for (let knot = 0; knot < 5; knot += 1) {
      ctx.strokeStyle = 'rgba(65,43,24,.2)';
      ctx.beginPath();
      ctx.ellipse(random() * 512, random() * 512, 13 + random() * 12, 4 + random() * 5, random() * .5, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(repeatX, repeatY);
  texture.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
  return texture;
}

function textureSurface(mesh, texture, roughness = .9) {
  mesh.material.map = texture;
  mesh.material.color.set(0xffffff);
  mesh.material.roughness = roughness;
  mesh.material.needsUpdate = true;
  return mesh;
}

function configureFloorTexture(texture, repeatX, repeatY, colorTexture = false) {
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(repeatX, repeatY);
  texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  if (colorTexture) texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

let coolFloorCanvas = null;
let coolFloorLoading = false;
let coolFloorCallbacks = [];

function coolConcreteTexture(sourceTexture, callback) {
  if (coolFloorCanvas) {
    sourceTexture.dispose();
    callback(new THREE.CanvasTexture(coolFloorCanvas));
    return;
  }
  coolFloorCallbacks.push(callback);
  if (coolFloorLoading) { sourceTexture.dispose(); return; }
  coolFloorLoading = true;
  const image = sourceTexture.image;
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let offset = 0; offset < pixels.data.length; offset += 4) {
    const red = pixels.data[offset];
    const green = pixels.data[offset + 1];
    const blue = pixels.data[offset + 2];
    const light = (red * .24) + (green * .62) + (blue * .14);
    // Keep the concrete neutral and only slightly warm so it sits naturally
    // beside the grey-green gallery walls without turning brown.
    pixels.data[offset] = Math.min(255, light * 1.025 + 38);
    pixels.data[offset + 1] = Math.min(255, light * 1.015 + 37);
    pixels.data[offset + 2] = Math.min(255, light * .995 + 35);
  }
  ctx.putImageData(pixels, 0, 0);
  coolFloorCanvas = canvas;
  coolFloorLoading = false;
  sourceTexture.dispose();
  const callbacks = coolFloorCallbacks.splice(0);
  callbacks.forEach(item => item(new THREE.CanvasTexture(coolFloorCanvas)));
}

function applyGalleryFloorMaterial(floor, width, depth) {
  const repeatX = Math.max(3.2, width / 5.2);
  const repeatY = Math.max(2.8, depth / 5.2);
  const base = './assets/textures/smooth_concrete_floor/';
  textureLoader.load(`${base}floor_diff_1k.jpg`, sourceTexture => {
    coolConcreteTexture(sourceTexture, texture => {
      if (!floor.parent) { texture.dispose(); return; }
      floor.material.map = configureFloorTexture(texture, repeatX, repeatY, true);
      floor.material.color.set(0xffffff);
      floor.material.needsUpdate = true;
    });
  });
  textureLoader.load(`${base}floor_normal_1k.jpg`, texture => {
    if (!floor.parent) { texture.dispose(); return; }
    floor.material.normalMap = configureFloorTexture(texture, repeatX, repeatY);
    floor.material.normalScale.set(.22, .22);
    floor.material.needsUpdate = true;
  });
  textureLoader.load(`${base}floor_rough_1k.jpg`, texture => {
    if (!floor.parent) { texture.dispose(); return; }
    floor.material.roughnessMap = configureFloorTexture(texture, repeatX, repeatY);
    floor.material.roughness = .76;
    floor.material.needsUpdate = true;
  });
}

let doorTextureMap = null;
function doorTexture() {
  if (doorTextureMap) return doorTextureMap;
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 1024;
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
  gradient.addColorStop(0, '#303532');
  gradient.addColorStop(.48, '#3b413d');
  gradient.addColorStop(1, '#2d322f');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  for (let x = 2; x < canvas.width; x += 5) {
    const alpha = .012 + ((x * 17) % 13) / 1300;
    ctx.fillStyle = `rgba(235,240,236,${alpha})`;
    ctx.fillRect(x, 0, 1, canvas.height);
  }
  doorTextureMap = new THREE.CanvasTexture(canvas);
  doorTextureMap.colorSpace = THREE.SRGBColorSpace;
  doorTextureMap.wrapS = doorTextureMap.wrapT = THREE.RepeatWrapping;
  doorTextureMap.repeat.set(1, 1);
  doorTextureMap.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
  return doorTextureMap;
}

function addDoorLeaf(parent) {
  const leaf = addBox(parent, [1.92, 3.7, .065], [0, 2.04, .235], 0x303632);
  textureSurface(leaf, doorTexture(), .76);
  const groove = 0x151917;
  // Three fine recessed joints replace the decorative outlined panels.
  addBasicBox(parent, [1.58, .018, .018], [0, 3.42, .276], groove);
  addBasicBox(parent, [1.58, .018, .018], [0, 2.34, .276], groove);
  addBasicBox(parent, [1.58, .018, .018], [0, .68, .276], groove);
  return leaf;
}

function softShadowTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 160;
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createRadialGradient(256, 80, 15, 256, 80, 250);
  gradient.addColorStop(0, 'rgba(0,0,0,.25)');
  gradient.addColorStop(.48, 'rgba(0,0,0,.13)');
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  return new THREE.CanvasTexture(canvas);
}

function roundedBoxGeometry(width, height, depth, radius = .06) {
  const shape = new THREE.Shape();
  const x = -width / 2, y = -height / 2;
  shape.moveTo(x + radius, y);
  shape.lineTo(x + width - radius, y);
  shape.quadraticCurveTo(x + width, y, x + width, y + radius);
  shape.lineTo(x + width, y + height - radius);
  shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  shape.lineTo(x + radius, y + height);
  shape.quadraticCurveTo(x, y + height, x, y + height - radius);
  shape.lineTo(x, y + radius);
  shape.quadraticCurveTo(x, y, x + radius, y);
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: depth - radius * 2,
    bevelEnabled: true,
    bevelSegments: 3,
    bevelSize: radius,
    bevelThickness: radius
  });
  geometry.translate(0, 0, -depth / 2 + radius);
  return geometry;
}

function plantLeafGeometry() {
  const shape = new THREE.Shape();
  shape.moveTo(0, 0);
  shape.bezierCurveTo(-.28, .13, -.31, .5, 0, .72);
  shape.bezierCurveTo(.31, .5, .28, .13, 0, 0);
  return new THREE.ShapeGeometry(shape, 10);
}

function canvasLabel(lines, options = {}) {
  const label = document.createElement('canvas');
  label.width = options.width || 1024;
  label.height = options.height || 320;
  const ctx = label.getContext('2d');
  ctx.fillStyle = options.background || '#f2f3f0';
  ctx.fillRect(0, 0, label.width, label.height);
  ctx.fillStyle = options.accent || '#b12e38';
  ctx.fillRect(0, 0, 18, label.height);
  ctx.fillStyle = options.color || '#151716';
  ctx.textBaseline = 'top';
  const compact = label.height <= 240;
  lines.forEach((line, index) => {
    ctx.font = index === 0 ? `600 ${compact ? 54 : 68}px Arial` : `400 ${compact ? 28 : 34}px Arial`;
    ctx.fillStyle = index === 0 ? (options.color || '#151716') : '#676d69';
    ctx.fillText(String(line || ''), compact ? 54 : 65, index === 0 ? (compact ? 27 : 45) : (compact ? 112 : 155) + ((index - 1) * (compact ? 39 : 52)), label.width - 100);
  });
  const texture = new THREE.CanvasTexture(label);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function artworkLabelTexture(artwork, accent) {
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 640;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#f4f4f1';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = accent || '#b12e38';
  ctx.fillRect(0, 0, 14, canvas.height);
  ctx.textBaseline = 'top';

  const writeWrapped = (text, x, y, maxWidth, lineHeight, maxLines) => {
    const words = String(text || '').trim().split(/\s+/).filter(Boolean);
    const lines = [];
    let line = '';
    words.forEach(word => {
      const candidate = line ? `${line} ${word}` : word;
      if (line && ctx.measureText(candidate).width > maxWidth) {
        lines.push(line);
        line = word;
      } else line = candidate;
    });
    if (line) lines.push(line);
    const visible = lines.slice(0, maxLines);
    if (lines.length > maxLines) {
      let last = visible[maxLines - 1];
      while (last && ctx.measureText(`${last}...`).width > maxWidth) last = last.slice(0, -1);
      visible[maxLines - 1] = `${last}...`;
    }
    visible.forEach((value, index) => ctx.fillText(value, x, y + index * lineHeight, maxWidth));
    return y + visible.length * lineHeight;
  };

  ctx.fillStyle = '#080909';
  ctx.font = '700 50px Arial';
  let cursorY = writeWrapped(artwork.title, 55, 54, 530, 59, 4) + 38;
  ctx.fillStyle = '#4f5552';
  ctx.font = '500 27px Arial';
  cursorY = writeWrapped(artwork.technique, 55, cursorY, 530, 34, 3) + 22;
  if (artwork.year) {
    ctx.fillStyle = '#8a8f8c';
    ctx.font = '400 23px Arial';
    ctx.fillText(String(artwork.year), 55, Math.min(cursorY, 555), 530);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function doorLabelTexture(label) {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 180;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#d5d9d6';
  ctx.font = '600 106px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(label || '').toUpperCase(), canvas.width / 2, canvas.height / 2, canvas.width - 40);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function floorProjectionTexture(label) {
  const canvas = document.createElement('canvas');
  canvas.width = 2048;
  canvas.height = 512;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = '600 166px Arial';
  ctx.shadowColor = 'rgba(244, 246, 239, .72)';
  ctx.shadowBlur = 34;
  ctx.fillStyle = 'rgba(246, 247, 242, .56)';
  ctx.fillText(label, canvas.width / 2, canvas.height / 2, canvas.width - 120);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
  return texture;
}

function roomProgressTexture(page, total) {
  const canvas = document.createElement('canvas');
  canvas.height = 256;
  const label = lang === 'en' ? `ROOM ${page} OF ${total}` : `SALLE ${page} SUR ${total}`;
  const sequence = `${label}    / / / / / /    `;
  let ctx = canvas.getContext('2d');
  ctx.font = '600 94px Arial';
  canvas.width = Math.ceil(ctx.measureText(sequence).width) + 8;
  ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.textBaseline = 'middle';
  ctx.font = '600 94px Arial';
  ctx.fillStyle = 'rgba(245, 246, 242, .78)';
  ctx.shadowColor = 'rgba(255, 255, 255, .62)';
  ctx.shadowBlur = 18;
  ctx.fillText(sequence, 4, canvas.height / 2);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.repeat.set(4, 1);
  texture.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
  return texture;
}

function addRoomProgressBand(page, total, width = 22, depth = 19) {
  if (total < 2) return;
  const texture = roomProgressTexture(page, total);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    toneMapped: false
  });
  const height = .68;
  const y = 5.03;
  const rear = new THREE.Mesh(new THREE.PlaneGeometry(width - .7, height), material);
  rear.position.set(0, y, (-depth / 2) + .14);
  const left = new THREE.Mesh(new THREE.PlaneGeometry(depth - .7, height), material.clone());
  left.position.set((-width / 2) + .14, y, 0);
  left.rotation.y = Math.PI / 2;
  const right = new THREE.Mesh(new THREE.PlaneGeometry(depth - .7, height), material.clone());
  right.position.set((width / 2) - .14, y, 0);
  right.rotation.y = -Math.PI / 2;
  [rear, left, right].forEach(mesh => {
    mesh.renderOrder = 2;
    roomRoot.add(mesh);
  });
  scrollingTextures.push(texture);
}

function hallWelcomeTexture() {
  const canvas = document.createElement('canvas');
  canvas.height = 256;
  const label = lang === 'en' ? "EXPLORE M'SIEUR C'S WORLD" : "EXPLOREZ L'UNIVERS M'SIEUR C";
  const sequence = `${label}    / / / / / /    `;
  let ctx = canvas.getContext('2d');
  ctx.font = '600 94px Arial';
  canvas.width = Math.ceil(ctx.measureText(sequence).width) + 8;
  ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.textBaseline = 'middle';
  ctx.font = '600 94px Arial';
  ctx.fillStyle = 'rgba(245, 246, 242, .78)';
  ctx.shadowColor = 'rgba(255, 255, 255, .62)';
  ctx.shadowBlur = 18;
  ctx.fillText(sequence, 4, canvas.height / 2);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.repeat.set(4, 1);
  texture.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
  return texture;
}

function addHallWelcomeBand(width = 25, depth = 22, frontZ = null) {
  const texture = hallWelcomeTexture();
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    toneMapped: false
  });
  const height = .68;
  const y = 5.03;
  const rear = new THREE.Mesh(new THREE.PlaneGeometry(width - .7, height), material);
  rear.position.set(0, y, (-depth / 2) + .14);
  const sideDepth = frontZ === null ? depth - .7 : frontZ + (depth / 2) - .28;
  const sideCenter = frontZ === null ? 0 : (frontZ - (depth / 2)) / 2;
  const left = new THREE.Mesh(new THREE.PlaneGeometry(sideDepth, height), material.clone());
  left.position.set((-width / 2) + .14, y, sideCenter);
  left.rotation.y = Math.PI / 2;
  const right = new THREE.Mesh(new THREE.PlaneGeometry(sideDepth, height), material.clone());
  right.position.set((width / 2) - .14, y, sideCenter);
  right.rotation.y = -Math.PI / 2;
  const walls = [rear, left, right];
  if (frontZ !== null) {
    const front = new THREE.Mesh(new THREE.PlaneGeometry(width - .7, height), material.clone());
    front.position.set(0, y, frontZ - .14);
    front.rotation.y = Math.PI;
    walls.push(front);
  }
  walls.forEach(mesh => {
    mesh.renderOrder = 2;
    roomRoot.add(mesh);
  });
  scrollingTextures.push(texture);
}

function clearRoom() {
  closeArtwork(false);
  clearSecretVideos();
  if (!roomRoot) return;
  roomRoot.traverse(object => {
    // The realistic hall doors are instances of one cached GLB. Their GPU
    // resources remain shared across room changes and are disposed on unload.
    if (object.userData.sharedDoorAsset) return;
    if (object.geometry) object.geometry.dispose();
    if (object.material) {
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      materials.forEach(item => {
        if (item.map && item.map !== doorTextureMap) item.map.dispose();
        if (item.normalMap) item.normalMap.dispose();
        if (item.roughnessMap) item.roughnessMap.dispose();
        item.dispose();
      });
    }
  });
  scene.remove(roomRoot);
  interactive = [];
  views = [];
  kineticObjects = [];
  scrollingTextures = [];
}

let realisticDoorSource = null;
let realisticDoorLoading = false;
let realisticDoorCallbacks = [];
let industrialPipeMaterial = null;
let industrialPipeLoading = false;
let industrialPipeCallbacks = [];

function recolorDoorTexture(texture, targetHex) {
  if (!texture?.image) return texture;
  const image = texture.image;
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const target = new THREE.Color(targetHex);
  for (let offset = 0; offset < pixels.data.length; offset += 4) {
    const light = (pixels.data[offset] * .24)
      + (pixels.data[offset + 1] * .62)
      + (pixels.data[offset + 2] * .14);
    // Preserve the baked patina while suppressing the original red/white
    // paint and its overexposed highlight strip.
    const shade = .56 + (Math.min(190, light) / 255) * .56;
    pixels.data[offset] = Math.min(255, target.r * 255 * shade);
    pixels.data[offset + 1] = Math.min(255, target.g * 255 * shade);
    pixels.data[offset + 2] = Math.min(255, target.b * 255 * shade);
  }
  ctx.putImageData(pixels, 0, 0);
  const result = new THREE.CanvasTexture(canvas);
  result.colorSpace = THREE.SRGBColorSpace;
  result.flipY = texture.flipY;
  result.wrapS = texture.wrapS;
  result.wrapT = texture.wrapT;
  result.magFilter = texture.magFilter;
  result.minFilter = texture.minFilter;
  result.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
  return result;
}

function prepareRealisticDoorMaterials(source) {
  const palette = {
    'Door-01': 0x69716c,
    'DoorFrame-01': 0x353b38,
    'Door-02': 0x505955,
    'DoorFrame-02': 0x292f2c
  };
  source.traverse(object => {
    if (!object.isMesh) return;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    const prepared = materials.map(material => {
      const copy = material.clone();
      const color = palette[copy.name] ?? 0x555d59;
      if (copy.map) copy.map = recolorDoorTexture(copy.map, color);
      copy.color.set(0xffffff);
      copy.roughness = Math.max(.52, copy.roughness ?? .65);
      return copy;
    });
    object.material = Array.isArray(object.material) ? prepared : prepared[0];
  });
}

function loadRealisticDoor(callback) {
  if (realisticDoorSource) {
    callback(realisticDoorSource);
    return;
  }
  realisticDoorCallbacks.push(callback);
  if (realisticDoorLoading) return;
  realisticDoorLoading = true;
  gltfLoader.load('./assets/models/gallery_doors/realistic/door_pack_web.glb', gltf => {
    realisticDoorSource = gltf.scene;
    prepareRealisticDoorMaterials(realisticDoorSource);
    realisticDoorSource.updateMatrixWorld(true);
    realisticDoorLoading = false;
    realisticDoorCallbacks.splice(0).forEach(item => item(realisticDoorSource));
  }, undefined, () => {
    realisticDoorLoading = false;
    realisticDoorCallbacks = [];
  });
}

function addRealisticHallDoor(parent, variant = 0) {
  loadRealisticDoor(source => {
    if (!parent.parent) return;
    const model = source.clone(true);
    const root = model.getObjectByName('RootNode');
    const selected = variant % 2 === 0
      ? new Set(['Door-01', 'DoorFrame-01'])
      : new Set(['Door-02', 'DoorFrame-02']);

    if (root) {
      [...root.children].forEach(child => {
        if (!selected.has(child.name)) root.remove(child);
      });
    }
    model.updateMatrixWorld(true);
    const bounds = new THREE.Box3().setFromObject(model);
    const size = bounds.getSize(new THREE.Vector3());
    const scale = 3.92 / Math.max(.001, size.y);
    model.scale.multiplyScalar(scale);
    model.updateMatrixWorld(true);

    const fitted = new THREE.Box3().setFromObject(model);
    const center = fitted.getCenter(new THREE.Vector3());
    model.position.x -= center.x;
    model.position.y += .08 - fitted.min.y;
    model.position.z += .34 - fitted.max.z;
    model.traverse(object => {
      object.userData.sharedDoorAsset = true;
      if (!object.isMesh) return;
      object.castShadow = true;
      object.receiveShadow = true;
    });
    parent.add(model);
  });

  // The source pack bakes the original dark knob into the door material.
  // A separate brushed-metal handle keeps the hardware legible after the
  // doors are recoloured and under the gallery's deliberately soft lighting.
  const handleMaterial = new THREE.MeshStandardMaterial({
    color: 0xc7c3b8,
    metalness: .86,
    roughness: .24
  });
  const rosette = new THREE.Mesh(new THREE.CylinderGeometry(.1, .1, .055, 24), handleMaterial);
  rosette.position.set(.72, 1.86, .405);
  rosette.rotation.x = Math.PI / 2;
  rosette.castShadow = true;
  parent.add(rosette);
  const lever = new THREE.Mesh(new THREE.CylinderGeometry(.032, .042, .36, 16), handleMaterial.clone());
  lever.position.set(.56, 1.86, .455);
  lever.rotation.z = Math.PI / 2;
  lever.castShadow = true;
  parent.add(lever);
  const stem = new THREE.Mesh(new THREE.CylinderGeometry(.035, .04, .095, 16), handleMaterial.clone());
  stem.position.set(.71, 1.86, .45);
  stem.rotation.x = Math.PI / 2;
  stem.castShadow = true;
  parent.add(stem);
}

function loadIndustrialPipeMaterial(callback) {
  if (industrialPipeMaterial) {
    callback(industrialPipeMaterial);
    return;
  }
  industrialPipeCallbacks.push(callback);
  if (industrialPipeLoading) return;
  industrialPipeLoading = true;
  gltfLoader.load('./assets/models/pipes/pipe_pack_web.glb', gltf => {
    let sourceMaterial = null;
    gltf.scene.traverse(object => {
      if (!sourceMaterial && object.isMesh && object.material) {
        sourceMaterial = Array.isArray(object.material) ? object.material[0] : object.material;
      }
    });
    industrialPipeMaterial = sourceMaterial || new THREE.MeshStandardMaterial({ color: 0x4a4b47 });
    industrialPipeLoading = false;
    industrialPipeCallbacks.splice(0).forEach(item => item(industrialPipeMaterial));
  }, undefined, () => {
    industrialPipeMaterial = new THREE.MeshStandardMaterial({ color: 0x4a4b47, roughness: .82, metalness: .28 });
    industrialPipeLoading = false;
    industrialPipeCallbacks.splice(0).forEach(item => item(industrialPipeMaterial));
  });
}

function pipeMaterial(source, tint = 0x77746d) {
  const material = source.clone();
  if (material.map) {
    material.map = material.map.clone();
    material.map.anisotropy = Math.min(4, renderer.capabilities.getMaxAnisotropy());
    material.map.needsUpdate = true;
  }
  material.color.set(tint);
  material.roughness = .78;
  material.metalness = .32;
  return material;
}

function addPipeRun(parent, points, material, radius = .055) {
  const vertices = points.map(point => new THREE.Vector3(...point));
  const curve = new THREE.CurvePath();
  let cursor = vertices[0];
  for (let index = 1; index < vertices.length - 1; index += 1) {
    const corner = vertices[index];
    const next = vertices[index + 1];
    const incomingLength = cursor.distanceTo(corner);
    const outgoingLength = corner.distanceTo(next);
    const bend = Math.min(.16, incomingLength * .32, outgoingLength * .32);
    const before = corner.clone().add(cursor.clone().sub(corner).normalize().multiplyScalar(bend));
    const after = corner.clone().add(next.clone().sub(corner).normalize().multiplyScalar(bend));
    if (cursor.distanceTo(before) > .001) curve.add(new THREE.LineCurve3(cursor, before));
    curve.add(new THREE.QuadraticBezierCurve3(before, corner, after));
    cursor = after;
  }
  curve.add(new THREE.LineCurve3(cursor, vertices[vertices.length - 1]));
  const pipe = new THREE.Mesh(new THREE.TubeGeometry(curve, 72, radius, 10, false), material);
  pipe.castShadow = false;
  pipe.receiveShadow = false;
  parent.add(pipe);
  return pipe;
}

function addPipeWallCollar(parent, position, material, radius = .115, sideWall = false) {
  const collar = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, .035, 20), material);
  collar.position.set(...position);
  if (sideWall) collar.rotation.z = Math.PI / 2;
  else collar.rotation.x = Math.PI / 2;
  collar.castShadow = false;
  parent.add(collar);
}

function addValveWheel(parent, position, rotationY, material) {
  const wheel = new THREE.Group();
  wheel.position.set(...position);
  wheel.rotation.y = rotationY;
  wheel.add(new THREE.Mesh(new THREE.TorusGeometry(.24, .028, 10, 36), material));
  for (let index = 0; index < 6; index += 1) {
    const spoke = new THREE.Mesh(new THREE.BoxGeometry(.38, .022, .022), material.clone());
    spoke.rotation.z = (Math.PI / 3) * index;
    wheel.add(spoke);
  }
  const hub = new THREE.Mesh(new THREE.CylinderGeometry(.055, .055, .1, 16), material.clone());
  hub.rotation.x = Math.PI / 2;
  wheel.add(hub);
  parent.add(wheel);
}

function addIndustrialHallPipes() {
  loadIndustrialPipeMaterial(source => {
    if (!roomRoot || currentRoom) return;
    const network = new THREE.Group();
    roomRoot.add(network);
    const material = pipeMaterial(source, 0x77736a);
    addPipeRun(network, [
      [-12.14, 5.14, 8.7], [-12.14, 5.14, -9.72],
      [-11.7, 5.42, -10.62], [11.7, 5.42, -10.62],
      [12.14, 5.14, -9.72], [12.14, 5.14, 8.7]
    ], material, .075);

    [
      { x: 0, z: -10.62, end: [0, 4.5, -10.95], rotation: 0, side: false },
      { x: -12.14, z: -.8, end: [-12.43, 4.42, -.8], rotation: Math.PI / 2, side: true },
      { x: 12.14, z: 2.6, end: [12.43, 4.58, 2.6], rotation: -Math.PI / 2, side: true }
    ].forEach((valve, index) => {
      const y = valve.end[1];
      const branchMaterial = pipeMaterial(source, index === 1 ? 0x66645f : 0x77736a);
      const onSide = valve.side;
      const wallPoint = onSide
        ? [valve.end[0] + (valve.x < 0 ? .29 : -.29), y, valve.z]
        : [valve.x, y, -10.62];
      addPipeRun(network, [
        [valve.x, onSide ? 5.14 : 5.42, valve.z],
        [valve.x, y + .19, valve.z], wallPoint, valve.end
      ], branchMaterial, .055);
      addPipeWallCollar(network, valve.end, branchMaterial.clone(), .105, onSide);
      const wheelPosition = onSide
        ? [valve.x + (valve.x < 0 ? .11 : -.11), y + .39, valve.z]
        : [valve.x, y + .39, -10.51];
      addValveWheel(network, wheelPosition, valve.rotation, branchMaterial.clone());
    });

    [
      { x: -12.14, z: -3.5, wallX: -12.43 },
      { x: -12.14, z: 4.15, wallX: -12.43 },
      { x: 12.14, z: -3.5, wallX: 12.43 },
      { x: 12.14, z: 4.15, wallX: 12.43 }
    ].forEach((anchor, index) => {
      const y = 4.72 - (index % 2) * .18;
      const branchMaterial = pipeMaterial(source, 0x6b6963);
      addPipeRun(network, [
        [anchor.x, 5.14, anchor.z], [anchor.x, y, anchor.z],
        [anchor.wallX, y, anchor.z]
      ], branchMaterial, .048);
      addPipeWallCollar(network, [anchor.wallX, y, anchor.z], branchMaterial.clone(), .09, true);
    });
  });
}

function clearSecretVideos() {
  secretVideoElements.forEach(video => {
    video.pause();
    video.removeAttribute('src');
    video.load();
    video.remove();
  });
  secretVideoElements = [];
}

function huntItems() {
  return exhibition?.hunt?.enabled ? (exhibition.hunt.items || []) : [];
}

function saveHuntProgress() {
  localStorage.setItem('msieurcExhibitionHunt', JSON.stringify(huntFound));
}

function updateHuntUI() {
  const total = huntItems().length;
  if (!ui.huntProgress || !total) return;
  huntFound = huntFound.filter(id => huntItems().some(item => Number(item.id) === Number(id)));
  ui.huntProgress.hidden = false;
  const remaining = Math.max(0, total - huntFound.length);
  ui.huntCount.textContent = remaining === 0
    ? (lang === 'en' ? 'Door open' : 'Porte ouverte')
    : `${remaining} ${lang === 'en' ? 'to find' : 'à trouver'}`;
  const progressLabel = ui.huntProgress.querySelector('small');
  if (progressLabel) progressLabel.textContent = `${huntFound.length} / ${total}`;
  ui.huntProgress.classList.toggle('is-complete', huntFound.length >= total);
}

function showHuntDialog(mode = 'intro') {
  if (!ui.huntDialog || !exhibition?.hunt?.enabled) return;
  const total = huntItems().length;
  const remaining = Math.max(0, total - huntFound.length);
  const complete = total > 0 && remaining === 0;
  ui.huntDialog.dataset.complete = complete ? 'true' : 'false';
  ui.huntReset.hidden = mode === 'intro' && huntFound.length === 0;
  if (mode === 'complete' || complete) {
    ui.huntKicker.textContent = lang === 'en' ? 'The passage is open' : 'Le passage est ouvert';
    ui.huntTitle.textContent = lang === 'en' ? 'The secret room awaits' : 'La salle secrète vous attend';
    ui.huntMessage.textContent = exhibition.hunt.completion;
    ui.huntClose.innerHTML = `${lang === 'en' ? 'Find the secret door' : 'Trouver la porte secrète'} <span>→</span>`;
  } else {
    ui.huntKicker.textContent = lang === 'en' ? 'A trail through the gallery' : 'Un jeu de piste dans la galerie';
    ui.huntTitle.textContent = mode === 'intro'
      ? (lang === 'en' ? 'Follow the white rabbit' : 'Suivez le lapin blanc')
      : `${remaining} ${lang === 'en' ? (remaining === 1 ? 'rabbit remains' : 'rabbits remain') : (remaining === 1 ? 'lapin à trouver' : 'lapins à trouver')}`;
    ui.huntMessage.textContent = mode === 'intro' ? exhibition.hunt.intro : (lang === 'en' ? 'Look carefully at the walls in every room.' : 'Observez attentivement les murs de chaque salle.');
    ui.huntClose.innerHTML = `${mode === 'intro' ? (lang === 'en' ? 'Start searching' : 'Commencer la recherche') : (lang === 'en' ? 'Continue' : 'Continuer')} <span>→</span>`;
  }
  ui.huntDialog.hidden = false;
}

function addHuntRabbits(room, page) {
  if (!exhibition?.hunt?.enabled) return;
  huntItems().filter(item => item.roomId === room.id && Number(item.page) === page && !huntFound.includes(Number(item.id))).forEach(item => {
    const horizontal = Math.max(.05, Math.min(.95, Number(item.horizontal) / 100));
    const height = .7 + Math.max(.15, Math.min(.9, Number(item.height) / 100)) * 4.6;
    const rabbitSrc = rabbitImageUrl(item.id);
    const rabbitMaterial = new THREE.MeshBasicMaterial({ transparent: true, side: THREE.DoubleSide, depthWrite: false, alphaTest: .04 });
    const rabbit = new THREE.Mesh(new THREE.PlaneGeometry(.46, .46), rabbitMaterial);
    textureLoader.load(rabbitSrc, texture => {
      texture.colorSpace = THREE.SRGBColorSpace;
      rabbitMaterial.map = texture;
      rabbitMaterial.needsUpdate = true;
      const aspect = texture.image.width / texture.image.height;
      rabbit.geometry.dispose();
      rabbit.geometry = new THREE.PlaneGeometry(aspect >= 1 ? .46 : .46 * aspect, aspect >= 1 ? .46 / aspect : .46);
    }, undefined, () => {});
    if (item.wall === 'left') {
      rabbit.position.set(-10.855, height, 5.8 - horizontal * 13.8);
      rabbit.rotation.y = Math.PI / 2;
    } else if (item.wall === 'right') {
      rabbit.position.set(10.855, height, -8 + horizontal * 13.8);
      rabbit.rotation.y = -Math.PI / 2;
    } else {
      rabbit.position.set(-8.7 + horizontal * 17.4, height, -9.36);
    }
    rabbit.userData = { type: 'hunt-rabbit', huntItem: item, rabbitSrc };
    interactive.push(rabbit);
    roomRoot.add(rabbit);
  });
}

function collectRabbit(object) {
  const item = object.userData.huntItem;
  if (!item || huntFound.includes(Number(item.id))) return;
  huntFound.push(Number(item.id));
  saveHuntProgress();
  object.visible = false;
  interactive = interactive.filter(candidate => candidate !== object);
  const feedback = document.createElement('span');
  feedback.className = 'hunt-rabbit-found';
  const feedbackImage = document.createElement('img');
  feedbackImage.src = object.userData.rabbitSrc || rabbitImageUrl(item.id);
  feedbackImage.alt = '';
  feedback.appendChild(feedbackImage);
  root.appendChild(feedback);
  setTimeout(() => feedback.remove(), 1100);
  updateHuntUI();
  if (huntFound.length >= huntItems().length) setTimeout(() => showHuntDialog('complete'), 900);
}

function secretProjectionTexture(title, message) {
  const canvas = document.createElement('canvas');
  canvas.width = 1600; canvas.height = 700;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.textAlign = 'center'; ctx.fillStyle = 'rgba(255,255,250,.94)';
  ctx.shadowColor = 'rgba(255,255,255,.72)'; ctx.shadowBlur = 25;
  ctx.font = '300 150px Dosis, sans-serif'; ctx.fillText(title, 800, 285);
  ctx.shadowBlur = 8; ctx.font = 'italic 34px Libre Baskerville, serif';
  const words = message.split(/\s+/); let line = ''; let y = 410;
  words.forEach(word => { const test = `${line}${word} `; if (ctx.measureText(test).width > 1160) { ctx.fillText(line.trim(), 800, y); line = `${word} `; y += 58; } else line = test; });
  ctx.fillText(line.trim(), 800, y);
  const texture = new THREE.CanvasTexture(canvas); texture.colorSpace = THREE.SRGBColorSpace; return texture;
}

function coverProjectionTexture(texture, mediaWidth, mediaHeight, frameWidth, frameHeight) {
  const mediaRatio = Math.max(1, mediaWidth) / Math.max(1, mediaHeight);
  const frameRatio = frameWidth / frameHeight;
  texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.repeat.set(1, 1);
  texture.offset.set(0, 0);
  if (mediaRatio > frameRatio) {
    texture.repeat.x = frameRatio / mediaRatio;
    texture.offset.x = (1 - texture.repeat.x) / 2;
  } else {
    texture.repeat.y = mediaRatio / frameRatio;
    texture.offset.y = (1 - texture.repeat.y) / 2;
  }
  texture.needsUpdate = true;
}

function addSecretMediaProjection(index, position, rotationY, width) {
  const height = 5.9;
  const projectionMaterial = new THREE.MeshBasicMaterial({ color: 0x050606, toneMapped: false });
  const projection = new THREE.Mesh(new THREE.PlaneGeometry(width, height), projectionMaterial);
  projection.position.copy(position);
  projection.rotation.y = rotationY;
  roomRoot.add(projection);

  const fallback = exhibition?.secretMedia?.fallbackImages?.[index];
  if (fallback) {
    textureLoader.load(fallback, texture => {
      texture.colorSpace = THREE.SRGBColorSpace;
      coverProjectionTexture(texture, texture.image.width, texture.image.height, width, height);
      projectionMaterial.map = texture;
      projectionMaterial.color.set(0xffffff);
      projectionMaterial.needsUpdate = true;
    }, undefined, () => {});
  }

  const source = exhibition?.secretMedia?.videos?.[index];
  if (!source) return;
  const video = document.createElement('video');
  video.src = source;
  video.muted = true;
  video.defaultMuted = true;
  video.loop = true;
  video.autoplay = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.disablePictureInPicture = true;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('aria-hidden', 'true');
  Object.assign(video.style, { position: 'fixed', width: '1px', height: '1px', opacity: '0', pointerEvents: 'none' });
  root.appendChild(video);
  secretVideoElements.push(video);
  video.addEventListener('loadedmetadata', () => {
    const texture = new THREE.VideoTexture(video);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    coverProjectionTexture(texture, video.videoWidth, video.videoHeight, width, height);
    if (projectionMaterial.map) projectionMaterial.map.dispose();
    projectionMaterial.map = texture;
    projectionMaterial.color.set(0xffffff);
    projectionMaterial.needsUpdate = true;
    video.play().catch(() => {});
  }, { once: true });
  video.load();
}

function createSecretVideos() {
  clearSecretVideos();
  addSecretMediaProjection(0, new THREE.Vector3(-9.84, 3, 0), Math.PI / 2, 17.55);
  addSecretMediaProjection(1, new THREE.Vector3(0, 3, -8.84), 0, 19.55);
  addSecretMediaProjection(2, new THREE.Vector3(9.84, 3, 0), -Math.PI / 2, 17.55);
}

function createSecretRoom() {
  clearRoom();
  secretRoom = { id: 'secret-room', secret: true, label: lang === 'en' ? 'Secret room' : 'Salle secrète', artworks: [] };
  currentRoom = secretRoom; currentArtworkPage = 0; currentView = 0;
  roomRoot = new THREE.Group(); scene.add(roomRoot);
  createArchitecture(20, 18, 0x171918, '#ecece7', false, false);
  createSecretVideos();
  const projection = new THREE.Mesh(new THREE.PlaneGeometry(13, 5.7), new THREE.MeshBasicMaterial({ map: secretProjectionTexture(exhibition.hunt.finalCode, exhibition.hunt.finalMessage), transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
  projection.position.set(0, 3.15, -8.78); roomRoot.add(projection);
  const glow = new THREE.PointLight(0xf4f1e6, 22, 12); glow.position.set(0, 3, -4.5); roomRoot.add(glow);
  views = [{ position: new THREE.Vector3(0, 1.7, 6.8), target: new THREE.Vector3(0, 3, -8.6) }];
  ui.location.textContent = secretRoom.label; ui.hall.hidden = false; ui.views.hidden = true;
  updateRoomNavigation(); moveToView(0, false);
}

function transitionToSecretRoom() {
  fadeToScene(createSecretRoom);
}

function createArchitecture(width = 22, depth = 19, wall = 0xe7e7e2, accent = '#b12e38', detailed = true, routeLine = true) {
  const floor = addBox(roomRoot, [width, .18, depth], [0, -.09, 0], 0x696c66);
  const ceiling = addBox(roomRoot, [width, .24, depth], [0, 6.02, 0], 0x252826);
  const rearWall = addBox(roomRoot, [width, 6, .25], [0, 3, -depth / 2], wall);
  const leftWall = addBox(roomRoot, [.25, 6, depth], [-width / 2, 3, 0], wall);
  const rightWall = addBox(roomRoot, [.25, 6, depth], [width / 2, 3, 0], wall);
  applyGalleryFloorMaterial(floor, width, depth);
  if (detailed) {
    textureSurface(ceiling, surfaceTexture('ceiling', '#232523', 5, 4), .92);
    textureSurface(rearWall, surfaceTexture('wall', '#737976', 5, 2), .98);
    textureSurface(leftWall, surfaceTexture('wall', '#7d837f', 4, 2), .98);
    textureSurface(rightWall, surfaceTexture('wall', '#686e6b', 4, 2), .98);
  }

  // Architectural baseboards and a discreet route line anchor the room.
  addBox(roomRoot, [width - .25, .16, .12], [0, .08, (-depth / 2) + .13], 0x171918);
  addBox(roomRoot, [.12, .16, depth - .25], [(-width / 2) + .13, .08, 0], 0x171918);
  addBox(roomRoot, [.12, .16, depth - .25], [(width / 2) - .13, .08, 0], 0x171918);
  if (routeLine) addBasicBox(roomRoot, [.055, .012, depth - 3], [0, .015, 0], accent);

  // Real fixtures are repeated from one cached Poly Haven model.
  [-width * .28, 0, width * .28].forEach(x => {
    addBox(roomRoot, [.07, .1, depth - 1.2], [x, 5.87, 0], 0x0e0f0f);
    placeModel(modelAssets.ceilingLight, {
      position: [x, 5.62, 0],
      rotation: [Math.PI / 2, 0, 0],
      targetWidth: 2.25,
      castShadow: false
    });
  });
  [-5.2, 0, 5.2].forEach(x => {
    const light = new THREE.SpotLight(0xfff4df, 24, 19, Math.PI / 5.5, .58, 1.25);
    light.position.set(x, 5.55, 1.5);
    light.target.position.set(x, 2.2, -depth / 2 + .5);
    roomRoot.add(light, light.target);
  });
}

function addHallRoomDoor(parent, room, variant = 0) {
  const opening = addBox(parent, [2.2, 4.05, .12], [0, 2.08, .12], 0x171a18);
  opening.material.transparent = true;
  opening.material.opacity = 0;
  opening.material.depthWrite = false;
  opening.userData = { type: 'room', room };
  interactive.push(opening);
  addRealisticHallDoor(parent, variant);
  addBox(parent, [2.48, .07, .42], [0, .035, .18], 0x8d8c84);
  addBasicBox(parent, [2.28, .018, .32], [0, .079, .2], room.accent);

  const sign = new THREE.Mesh(new THREE.PlaneGeometry(3.05, .46), new THREE.MeshBasicMaterial({
    map: doorLabelTexture(room.label),
    transparent: true,
    side: THREE.DoubleSide
  }));
  sign.position.set(1.85, 2.15, .205);
  sign.rotation.z = -Math.PI / 2;
  sign.userData.isDoorLabel = true;
  parent.add(sign);
}

function addSecretHallDoor(parent, room) {
  const opening = addBox(parent, [3.15, 4.35, .16], [0, 2.18, .14], 0x171a18);
  opening.material.transparent = true;
  opening.material.opacity = 0;
  opening.material.depthWrite = false;
  opening.userData = { type: 'room', room };
  interactive.push(opening);

  // A matte backing closes the transparent panes and suggests darkness beyond.
  const darkBacking = addBox(parent, [2.34, 3.72, .06], [0, 2.02, -.035], 0x030404);
  darkBacking.material.roughness = 1;
  placeModel(modelAssets.secretDoor, {
    parent,
    position: [0, 0, .1],
    rotation: [0, Math.PI, 0],
    targetHeight: 4.12
  });
  addBox(parent, [2.72, .07, .42], [0, .035, .13], 0x777b76);
  const doorLight = new THREE.SpotLight(0xffe7c2, 7, 7, Math.PI / 6.5, .66, 1.4);
  doorLight.position.set(0, 4.85, 2.45);
  doorLight.target.position.set(0, 1.95, .02);
  parent.add(doorLight, doorLight.target);
  const sign = new THREE.Mesh(new THREE.PlaneGeometry(3.25, .48), new THREE.MeshBasicMaterial({
    map: doorLabelTexture(room.label),
    transparent: true,
    side: THREE.DoubleSide
  }));
  sign.position.set(1.95, 2.12, .24);
  sign.rotation.z = -Math.PI / 2;
  sign.userData.isDoorLabel = true;
  parent.add(sign);
}

function corridorTexture(accent = '#808784') {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 1600;
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, '#202422');
  gradient.addColorStop(.5, '#0b0d0c');
  gradient.addColorStop(1, '#171a18');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const vanishingX = canvas.width / 2;
  const vanishingY = 770;
  ctx.fillStyle = '#343936';
  ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(vanishingX - 44, vanishingY); ctx.lineTo(0, canvas.height); ctx.closePath(); ctx.fill();
  ctx.fillStyle = '#292e2b';
  ctx.beginPath(); ctx.moveTo(canvas.width, 0); ctx.lineTo(vanishingX + 44, vanishingY); ctx.lineTo(canvas.width, canvas.height); ctx.closePath(); ctx.fill();
  ctx.fillStyle = '#181b19';
  ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(canvas.width, 0); ctx.lineTo(vanishingX + 44, vanishingY); ctx.lineTo(vanishingX - 44, vanishingY); ctx.closePath(); ctx.fill();
  ctx.fillStyle = '#242825';
  ctx.beginPath(); ctx.moveTo(0, canvas.height); ctx.lineTo(canvas.width, canvas.height); ctx.lineTo(vanishingX + 44, vanishingY); ctx.lineTo(vanishingX - 44, vanishingY); ctx.closePath(); ctx.fill();
  for (let step = 0; step < 7; step += 1) {
    const progress = step / 7;
    const y = 120 + progress * 590;
    const halfWidth = 330 * (1 - progress) + 42;
    ctx.strokeStyle = `rgba(235,238,232,${.44 - progress * .04})`;
    ctx.lineWidth = Math.max(3, 17 * (1 - progress));
    ctx.beginPath(); ctx.moveTo(vanishingX - halfWidth, y); ctx.lineTo(vanishingX + halfWidth, y); ctx.stroke();
  }
  ctx.fillStyle = accent;
  ctx.fillRect(0, canvas.height - 22, canvas.width, 22);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
  return texture;
}

function addHallRoomPassage(parent, room) {
  const passage = new THREE.Mesh(
    new THREE.PlaneGeometry(2.35, 4.15),
    new THREE.MeshBasicMaterial({ map: corridorTexture(room.accent), side: THREE.DoubleSide })
  );
  passage.position.set(0, 2.08, .23);
  passage.userData = { type: 'room', room };
  parent.add(passage);
  interactive.push(passage);
  const frameParts = [
    addBox(parent, [.12, 4.28, .3], [-1.22, 2.14, .03], 0x3c423e),
    addBox(parent, [.12, 4.28, .3], [1.22, 2.14, .03], 0x3c423e),
    addBox(parent, [2.56, .14, .3], [0, 4.26, .03], 0x3c423e)
  ];
  frameParts.forEach(part => {
    part.userData = { type: 'room', room };
    interactive.push(part);
  });
  addBox(parent, [2.5, .055, .4], [0, .03, .17], 0x777b76);
  const sign = new THREE.Mesh(new THREE.PlaneGeometry(3.05, .46), new THREE.MeshBasicMaterial({
    map: doorLabelTexture(room.label), transparent: true, side: THREE.DoubleSide
  }));
  sign.position.set(1.85, 2.15, .205);
  sign.rotation.z = -Math.PI / 2;
  sign.userData.isDoorLabel = true;
  parent.add(sign);
}

function createHall() {
  clearRoom();
  clearTimeout(viewPromptTimer);
  ui.views.classList.remove('is-discovering');
  currentRoom = null;
  currentView = 0;
  roomRoot = new THREE.Group();
  scene.add(roomRoot);
  createArchitecture(25, 22, 0x747a77, '#b12e38', false, false);
  const hallRearExtension = 4;
  const hallBackZ = 15;
  const extensionFloor = addBox(roomRoot, [25, .18, hallRearExtension], [0, -.09, 13], 0x696c66);
  applyGalleryFloorMaterial(extensionFloor, 25, hallRearExtension);
  addBox(roomRoot, [25, .24, hallRearExtension], [0, 6.02, 13], 0x252826);
  addBox(roomRoot, [.25, 6, hallRearExtension + .25], [-12.5, 3, 12.875], 0x747a77);
  addBox(roomRoot, [.25, 6, hallRearExtension + .25], [12.5, 3, 12.875], 0x747a77);
  addBox(roomRoot, [25, 6, .25], [0, 3, hallBackZ], 0x747a77);
  addBox(roomRoot, [25, .16, .12], [0, .08, hallBackZ - .13], 0x171918);
  addBox(roomRoot, [.12, .16, hallRearExtension + .3], [-12.37, .08, 12.85], 0x171918);
  addBox(roomRoot, [.12, .16, hallRearExtension + .3], [12.37, .08, 12.85], 0x171918);
  addHallWelcomeBand(25, 22, hallBackZ);

  // A kinetic monogram gives the hall its own identity.
  const sculpture = new THREE.Group();
  sculpture.position.set(0, 1.48, 1.15);
  placeModel(modelAssets.pedestal, {
    position: [0, 0, 1.15],
    targetWidth: 1.08,
    onLoad: anchor => {
      const bounds = new THREE.Box3().setFromObject(anchor);
      sculpture.position.y = bounds.max.y + .53;
    }
  });
  placeModel(modelAssets.extinguisher, {
    position: [11.62, .68, -10.66],
    rotation: [0, Math.PI, 0],
    targetHeight: .92
  });
  sculpture.rotation.z = -.28;
  const cShape = new THREE.Mesh(new THREE.TorusGeometry(.43, .075, 20, 72, Math.PI * 1.68), new THREE.MeshStandardMaterial({ color: 0xf7f7f3, roughness: .38, metalness: .18 }));
  cShape.rotation.z = .5;
  sculpture.add(cShape);
  const sculptureLight = new THREE.PointLight(0xfff4df, 7, 5);
  sculptureLight.position.set(0, .6, 1.1);
  sculpture.add(sculptureLight);
  roomRoot.add(sculpture);
  kineticObjects.push(sculpture);

  exhibition.rooms.forEach((room, index) => {
    let portalPosition;
    let portalRotation = 0;
    if (index < 4) {
      portalPosition = [([-8.25, -2.75, 2.75, 8.25])[index], 0, -10.72];
    } else {
      const sideIndex = index - 4;
      const onLeft = sideIndex % 2 === 0;
      const row = Math.floor(sideIndex / 2);
      const depthPosition = [-7.1, 0, 7.1][row];
      portalPosition = [onLeft ? -12.3 : 12.3, 0, depthPosition];
      portalRotation = onLeft ? Math.PI / 2 : -Math.PI / 2;
    }
    const portal = new THREE.Group();
    portal.position.set(...portalPosition);
    portal.rotation.y = portalRotation;
    roomRoot.add(portal);
    addHallRoomDoor(portal, room, index % 2);
  });
  if (exhibition?.hunt?.enabled && huntItems().length > 0 && huntFound.length >= huntItems().length) {
    const secretPortal = new THREE.Group();
    secretPortal.position.set(0, 0, hallBackZ - .28);
    secretPortal.rotation.y = Math.PI;
    roomRoot.add(secretPortal);
    addSecretHallDoor(secretPortal, { id: 'secret-room', secret: true, label: lang === 'en' ? 'Secret room' : 'Salle secrète', accent: '#e8e8e3' });
  }
  views = [
    { position: new THREE.Vector3(0, 1.7, 6.25), target: new THREE.Vector3(0, 2.55, -9.2) },
    { position: new THREE.Vector3(0, 1.7, 3.4), target: new THREE.Vector3(-11.7, 2.3, -1.5) },
    { position: new THREE.Vector3(0, 1.7, -2.4), target: new THREE.Vector3(11.7, 2.3, -3.5) },
    { position: new THREE.Vector3(0, 1.7, -6.6), target: new THREE.Vector3(0, 2.5, -10.5) }
  ];
  if (exhibition?.hunt?.enabled && huntItems().length > 0 && huntFound.length >= huntItems().length) {
    views.push({ position: new THREE.Vector3(0, 1.7, 6.4), target: new THREE.Vector3(0, 2.35, hallBackZ - .2) });
  }
  ui.location.textContent = copy.hall;
  ui.hall.hidden = true;
  ui.views.hidden = views.length < 2;
  updateRoomNavigation();
  moveToView(0, false);
}

function artworkPlacement(index) {
  const placements = [
    { p: [-5.2, 2.75, -9.3], r: [0, 0, 0], n: [0, 0, 1] },
    { p: [0, 2.75, -9.3], r: [0, 0, 0], n: [0, 0, 1] },
    { p: [5.2, 2.75, -9.3], r: [0, 0, 0], n: [0, 0, 1] },
    { p: [-10.7, 2.75, -4.4], r: [0, Math.PI / 2, 0], n: [1, 0, 0] },
    { p: [-10.7, 2.75, 2], r: [0, Math.PI / 2, 0], n: [1, 0, 0] },
    { p: [10.7, 2.75, -4.4], r: [0, -Math.PI / 2, 0], n: [-1, 0, 0] },
    { p: [10.7, 2.75, 2], r: [0, -Math.PI / 2, 0], n: [-1, 0, 0] },
    { p: [0, 2.75, 5], r: [0, Math.PI, 0], n: [0, 0, -1] }
  ];
  return placements[index % placements.length];
}

function artworkFrameSize(artwork, landscape = false) {
  let widthCm = Number(artwork.displayWidthCm) || 60;
  let heightCm = Number(artwork.displayHeightCm) || 80;
  if (landscape && heightCm > widthCm) [widthCm, heightCm] = [heightCm, widthCm];
  if (!landscape && widthCm > heightCm && widthCm / heightCm > 1.15) [widthCm, heightCm] = [heightCm, widthCm];
  const longestSide = Math.max(widthCm, heightCm);
  let displayedLongestSide;
  if (longestSide >= 120) displayedLongestSide = 3.3;
  else if (longestSide >= 75) displayedLongestSide = 2.35;
  else if (longestSide >= 60) displayedLongestSide = 2.15;
  else if (longestSide >= 40) displayedLongestSide = 1.95;
  else if (longestSide >= 28) displayedLongestSide = 1.7;
  else if (longestSide >= 20) displayedLongestSide = 1.45;
  else displayedLongestSide = Math.max(1.2, longestSide / 16);
  const scale = displayedLongestSide / longestSide;
  return {
    width: Math.max(.72, widthCm * scale),
    height: Math.max(.92, heightCm * scale)
  };
}

function addArtwork(artwork, index, accent) {
  const placement = artworkPlacement(index);
  const group = new THREE.Group();
  group.position.set(...placement.p);
  group.rotation.set(...placement.r);
  group.userData = { type: 'artwork', artwork, normal: new THREE.Vector3(0, 0, 1) };
  roomRoot.add(group);
  const artworkLight = new THREE.SpotLight(0xfff2dc, 11, 8, Math.PI / 6, .62, 1.25);
  artworkLight.position.set(0, 2.75, 1.35);
  artworkLight.target.position.set(0, 0, 0);
  group.add(artworkLight, artworkLight.target);
  const fixture = new THREE.Mesh(new THREE.CylinderGeometry(.09, .13, .34, 16), material(0x111313));
  fixture.position.set(0, 2.72, 1.38);
  fixture.rotation.x = Math.PI / 2;
  group.add(fixture);
  let frameSize = artworkFrameSize(artwork);
  const hasPaperFrame = ['a4', 'a5'].includes(String(artwork.exhibitionFormat || '').toLowerCase());
  // A4 and A5 works receive a traditional black frame and white mount.
  const frame = addBox(group, [frameSize.width + (hasPaperFrame ? .14 : .06), frameSize.height + (hasPaperFrame ? .14 : .06), .08], [0, 0, 0], hasPaperFrame ? 0x111212 : 0xe8e7e2);
  frame.userData = group.userData;
  interactive.push(frame);
  const mount = hasPaperFrame
    ? new THREE.Mesh(new THREE.PlaneGeometry(frameSize.width, frameSize.height), new THREE.MeshBasicMaterial({ color: 0xf7f6f1 }))
    : null;
  if (mount) {
    mount.position.z = .045;
    mount.userData = group.userData;
    interactive.push(mount);
    group.add(mount);
  }
  const art = new THREE.Mesh(new THREE.PlaneGeometry(frameSize.width, frameSize.height), new THREE.MeshBasicMaterial({ color: 0x3b3d3c }));
  art.position.z = hasPaperFrame ? .052 : .045;
  art.userData = group.userData;
  interactive.push(art);
  group.add(art);
  let label = null;
  const applyArtworkTexture = texture => {
    texture.colorSpace = THREE.SRGBColorSpace;
    const aspect = texture.image.width / texture.image.height;
    frameSize = artworkFrameSize(artwork, aspect > 1.08);
    frame.geometry.dispose();
    frame.geometry = new THREE.BoxGeometry(frameSize.width + (hasPaperFrame ? .14 : .06), frameSize.height + (hasPaperFrame ? .14 : .06), .08);
    if (mount) {
      mount.geometry.dispose();
      mount.geometry = new THREE.PlaneGeometry(frameSize.width, frameSize.height);
    }
    const maxW = hasPaperFrame ? frameSize.width * .76 : frameSize.width;
    const maxH = hasPaperFrame ? frameSize.height * .76 : frameSize.height;
    let width = maxW, height = width / aspect;
    if (height > maxH) { height = maxH; width = height * aspect; }
    art.geometry.dispose();
    art.geometry = new THREE.PlaneGeometry(width, height);
    art.material.map = texture;
    art.material.color.set(0xffffff);
    art.material.needsUpdate = true;
    if (label) {
      label.position.set((frameSize.width / 2) + .56, -.08, .08);
    }
  };
  group.userData.applyTexture = applyArtworkTexture;
  textureLoader.load(artwork.thumbnail, applyArtworkTexture, undefined, () => {});
  label = new THREE.Mesh(new THREE.PlaneGeometry(.56, .56), new THREE.MeshBasicMaterial({
    map: artworkLabelTexture(artwork, accent),
    side: THREE.DoubleSide
  }));
  label.position.set((frameSize.width / 2) + .56, -.08, .08);
  label.userData = group.userData;
  interactive.push(label);
  group.add(label);
}

function addRoomDoor(room, side, label) {
  const group = new THREE.Group();
  group.position.set(side < 0 ? -10.72 : 10.72, 0, 6.65);
  group.rotation.y = side < 0 ? Math.PI / 2 : -Math.PI / 2;
  roomRoot.add(group);
  const opening = addBox(group, [2.2, 4.05, .12], [0, 2.08, .16], 0x171a18);
  opening.material.transparent = true;
  opening.material.opacity = 0;
  opening.material.depthWrite = false;
  opening.userData = { type: 'room', room };
  interactive.push(opening);
  addRealisticHallDoor(group, 1);
  addBox(group, [2.48, .07, .42], [0, .035, .18], 0x8d8c84);
  addBasicBox(group, [2.28, .018, .32], [0, .079, .2], room.accent);
  const passageLight = new THREE.PointLight(0xffedcf, 2.4, 3.6, 2);
  passageLight.position.set(0, 3.72, .5);
  group.add(passageLight);
  const sign = new THREE.Mesh(new THREE.PlaneGeometry(3.05, .46), new THREE.MeshBasicMaterial({
    map: doorLabelTexture(label),
    transparent: true,
    side: THREE.DoubleSide
  }));
  sign.position.set(side < 0 ? 1.55 : -1.55, 2.15, .205);
  sign.rotation.z = side < 0 ? -Math.PI / 2 : Math.PI / 2;
  sign.userData.isDoorLabel = true;
  group.add(sign);
}

function setDoorLabelsVisible(visible) {
  if (!roomRoot) return;
  roomRoot.traverse(object => {
    if (object.userData.isDoorLabel) object.visible = visible;
  });
}

function addRoomAccessory(roomIndex) {
  const side = roomIndex % 2 === 0 ? 1 : -1;
  const x = side * 3.35;
  const z = 1.22;
  const variants = [
    { asset: modelAssets.plantSoft, height: 1.55 },
    { asset: modelAssets.ottoman, width: 1.08 },
    { asset: modelAssets.plantSmall, height: 1.12 },
    { asset: modelAssets.armchair, width: 1.18 },
    { asset: modelAssets.plantSoft, height: 1.42 },
    { asset: modelAssets.ottoman, width: 1.02 }
  ];
  const variant = variants[roomIndex % variants.length];
  placeModel(variant.asset, {
    position: [x, 0, z],
    rotation: [0, side < 0 ? .35 : -.35, 0],
    targetWidth: variant.width,
    targetHeight: variant.height
  });
}

function createRoom(room, artworkPage = 0) {
  clearRoom();
  currentRoom = room;
  currentView = 0;
  const pageCount = Math.max(1, Math.ceil(room.artworks.length / 7));
  currentArtworkPage = Math.max(0, Math.min(pageCount - 1, artworkPage));
  roomRoot = new THREE.Group();
  scene.add(roomRoot);
  createArchitecture(22, 19, 0x747a77, room.accent);
  addRoomProgressBand(currentArtworkPage + 1, pageCount, 22, 19);
  room.artworks.slice(currentArtworkPage * 7, (currentArtworkPage + 1) * 7).forEach((artwork, index) => addArtwork(artwork, index, room.accent));
  addHuntRabbits(room, currentArtworkPage + 1);
  placeModel(modelAssets.bench, {
    position: [0, 0, 1.25],
    rotation: [0, 0, 0],
    targetHeight: .78,
    scaleAxes: [3.4, 1, 1]
  });
  const benchShadow = new THREE.Mesh(new THREE.PlaneGeometry(4.85, 1.45), new THREE.MeshBasicMaterial({
    map: softShadowTexture(), transparent: true, depthWrite: false, opacity: .72
  }));
  benchShadow.position.set(0, .012, 1.25);
  benchShadow.rotation.x = -Math.PI / 2;
  roomRoot.add(benchShadow);
  const roomIndex = exhibition.rooms.findIndex(item => item.id === room.id);
  addRoomAccessory(roomIndex);
  addBasicBox(roomRoot, [3.6, .018, .04], [0, .025, -5.8], room.accent);
  const previousRoom = exhibition.rooms[(roomIndex - 1 + exhibition.rooms.length) % exhibition.rooms.length];
  const nextRoom = exhibition.rooms[(roomIndex + 1) % exhibition.rooms.length];
  addRoomDoor(previousRoom, -1, previousRoom.label);
  addRoomDoor(nextRoom, 1, nextRoom.label);
  views = [
    { position: new THREE.Vector3(0, 1.7, 6.9), target: new THREE.Vector3(0, 2.2, -8) },
    { position: new THREE.Vector3(-2.2, 1.7, -1.5), target: new THREE.Vector3(-10.5, 2.4, -1.5) },
    { position: new THREE.Vector3(2.2, 1.7, -1.5), target: new THREE.Vector3(10.5, 2.4, -1.5) },
    { position: new THREE.Vector3(0, 1.7, -4.5), target: new THREE.Vector3(0, 2.2, 4.5) }
  ];
  ui.location.textContent = room.label;
  ui.hall.hidden = false;
  ui.views.hidden = pageCount < 2;
  ui.views.classList.toggle('is-discovering', pageCount > 1 && currentArtworkPage === 0);
  clearTimeout(viewPromptTimer);
  if (pageCount > 1) {
    viewPromptTimer = window.setTimeout(() => ui.views.classList.remove('is-discovering'), 4200);
  }
  updateRoomNavigation();
  moveToView(0, false);
}

function updateRoomNavigation() {
  ui.rooms.innerHTML = '';
  const hallButton = document.createElement('button');
  hallButton.type = 'button';
  hallButton.textContent = copy.hall;
  hallButton.setAttribute('aria-current', String(!currentRoom));
  hallButton.addEventListener('click', () => transitionTo(null));
  ui.rooms.append(hallButton);
  exhibition.rooms.forEach(room => {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = room.label;
    button.setAttribute('aria-current', String(currentRoom?.id === room.id));
    button.addEventListener('click', () => transitionTo(room));
    ui.rooms.append(button);
  });
}

function transitionTo(room) {
  if (room?.secret) { transitionToSecretRoom(); return; }
  if (currentRoom?.id === room?.id || (!currentRoom && !room)) return;
  fadeToScene(() => { room ? createRoom(room, 0) : createHall(); });
}

function moveToView(index, animate = true) {
  if (!views.length) return;
  currentView = (index + views.length) % views.length;
  const view = views[currentView];
  const compactNavigation = matchMedia('(max-width: 800px)').matches;
  if (currentRoom) {
    const pageCount = Math.max(1, Math.ceil(currentRoom.artworks.length / 7));
    ui.viewLabel.textContent = `${pageCount} ${copy.discover}`;
    ui.viewCount.textContent = compactNavigation
      ? `${currentArtworkPage + 1} / ${pageCount}`
      : `${copy.roomProgress} ${currentArtworkPage + 1} ${copy.of} ${pageCount}`;
    ui.viewDots.innerHTML = Array.from({ length: pageCount }, (_, dotIndex) => `<i class="${dotIndex === currentArtworkPage ? 'is-active' : ''}"></i>`).join('');
  } else {
    ui.viewLabel.textContent = copy.hall;
    ui.viewCount.textContent = compactNavigation
      ? `${currentView + 1} / ${views.length}`
      : `${copy.viewProgress} ${currentView + 1} ${copy.of} ${views.length}`;
    ui.viewDots.innerHTML = Array.from({ length: views.length }, (_, dotIndex) => `<i class="${dotIndex === currentView ? 'is-active' : ''}"></i>`).join('');
  }
  lookYaw = Math.atan2(view.target.x - view.position.x, -(view.target.z - view.position.z));
  lookPitch = Math.atan2(view.target.y - view.position.y, view.position.distanceTo(new THREE.Vector3(view.target.x, view.position.y, view.target.z)));
  if (!animate) { camera.position.copy(view.position); applyLook(); return; }
  tween = { start: performance.now(), duration: 900, from: camera.position.clone(), to: view.position.clone() };
}

function applyLook() {
  lookPitch = THREE.MathUtils.clamp(lookPitch, -.55, .55);
  const direction = new THREE.Vector3(Math.sin(lookYaw) * Math.cos(lookPitch), Math.sin(lookPitch), -Math.cos(lookYaw) * Math.cos(lookPitch));
  camera.lookAt(camera.position.clone().add(direction));
}

function focusArtwork(object) {
  const data = object.userData;
  if (!data.artwork) return;
  if (!selectedArtwork) {
    artworkReturnView = {
      position: camera.position.clone(),
      yaw: lookYaw,
      pitch: lookPitch
    };
  }
  selectedArtwork = data.artwork;
  setDoorLabelsVisible(false);
  if (!data.highResolutionRequested && data.applyTexture) {
    data.highResolutionRequested = true;
    textureLoader.load(data.artwork.image, data.applyTexture, undefined, () => {});
  }
  const world = new THREE.Vector3();
  object.getWorldPosition(world);
  const normal = data.normal.clone().applyQuaternion(object.parent.getWorldQuaternion(new THREE.Quaternion())).normalize();
  const isLandscapeMobile = matchMedia('(orientation: landscape) and (pointer: coarse)').matches;
  const isMobile = matchMedia('(max-width: 800px)').matches || isLandscapeMobile;
  const isPaperFormat = ['a4', 'a5'].includes(String(data.artwork.exhibitionFormat || '').toLowerCase());
  const focusDistance = isMobile ? (isPaperFormat ? 3.65 : 4.5) : 3.4;
  const destination = world.clone().add(normal.multiplyScalar(focusDistance));
  destination.y = world.y;
  tween = { start: performance.now(), duration: 750, from: camera.position.clone(), to: destination };
  const focusTarget = world.clone();
  if (isLandscapeMobile) {
    // The details occupy the right-hand 40%: aim slightly to the artwork's
    // right so the frame is centred in the remaining 60% of the viewport.
    const screenRight = new THREE.Vector3(0, 1, 0).cross(normal).normalize();
    focusTarget.add(screenRight.multiplyScalar(focusDistance * .34));
  }
  lookYaw = Math.atan2(focusTarget.x - destination.x, -(focusTarget.z - destination.z));
  lookPitch = isMobile ? -.14 : 0;
  ui.reference.textContent = [data.artwork.reference, data.artwork.year].filter(Boolean).join(' · ');
  ui.title.textContent = data.artwork.title;
  ui.technique.textContent = data.artwork.technique || copy.unknown;
  ui.support.textContent = data.artwork.support || copy.unknown;
  ui.dimensions.textContent = data.artwork.dimensions || copy.unknown;
  ui.link.href = data.artwork.url;
  updateArtworkNavigation();
  ui.panel.classList.add('is-open');
  ui.panel.setAttribute('aria-hidden', 'false');
  ui.artworkBrowse.classList.add('is-open');
  celebrateArtwork(data.artwork);
}

function orderedRoomArtworks() {
  if (!currentRoom) return [];
  // Physical hanging order: start beside the left-hand door, follow the left
  // wall, cross the rear wall, then finish along the right wall by its door.
  const placementOrder = [4, 3, 0, 1, 2, 5, 6];
  const ordered = [];
  for (let pageStart = 0; pageStart < currentRoom.artworks.length; pageStart += 7) {
    const page = currentRoom.artworks.slice(pageStart, pageStart + 7);
    placementOrder.forEach(placementIndex => {
      if (page[placementIndex]) ordered.push(page[placementIndex]);
    });
  }
  return ordered;
}

function artworkIndex(artwork) {
  if (!currentRoom || !artwork) return -1;
  return orderedRoomArtworks().findIndex(item => String(item.id) === String(artwork.id));
}

function artworkObject(artwork) {
  return interactive.find(object => object.userData.type === 'artwork'
    && String(object.userData.artwork?.id) === String(artwork.id));
}

function updateArtworkNavigation() {
  const total = currentRoom?.artworks.length || 0;
  const index = artworkIndex(selectedArtwork);
  ui.artworkBrowse.hidden = total < 2;
  ui.artworkPosition.textContent = index >= 0
    ? `${String(index + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`
    : '';
}

function navigateArtwork(direction) {
  if (!selectedArtwork || !currentRoom || artworkNavigationPending) return;
  const orderedArtworks = orderedRoomArtworks();
  const total = orderedArtworks.length;
  const index = artworkIndex(selectedArtwork);
  if (total < 2 || index < 0) return;
  const targetIndex = (index + direction + total) % total;
  const targetArtwork = orderedArtworks[targetIndex];
  const sourceIndex = currentRoom.artworks.findIndex(item => String(item.id) === String(targetArtwork.id));
  const targetPage = Math.floor(sourceIndex / 7);
  if (targetPage === currentArtworkPage) {
    const targetObject = artworkObject(targetArtwork);
    if (targetObject) focusArtwork(targetObject);
    return;
  }
  artworkNavigationPending = true;
  ui.curtain.classList.add('is-active');
  setTimeout(() => {
    createRoom(currentRoom, targetPage);
    const targetObject = artworkObject(targetArtwork);
    if (targetObject) focusArtwork(targetObject);
    setTimeout(() => {
      ui.curtain.classList.remove('is-active');
      artworkNavigationPending = false;
    }, 180);
  }, 560);
}

function closeArtwork(moveBack = true) {
  ui.panel.classList.remove('is-open');
  ui.panel.setAttribute('aria-hidden', 'true');
  ui.artworkBrowse.classList.remove('is-open');
  ui.artworkBrowse.hidden = true;
  selectedArtwork = null;
  setDoorLabelsVisible(true);
  const returnView = artworkReturnView;
  artworkReturnView = null;
  if (moveBack && returnView) {
    lookYaw = returnView.yaw;
    lookPitch = returnView.pitch;
    tween = {
      start: performance.now(),
      duration: 750,
      from: camera.position.clone(),
      to: returnView.position
    };
  }
}

function pick(event) {
  const rect = canvas.getBoundingClientRect();
  pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(pointer, camera);
  const hit = raycaster.intersectObjects(interactive, false)[0];
  if (!hit) return;
  if (hit.object.userData.type === 'room') transitionTo(hit.object.userData.room);
  if (hit.object.userData.type === 'artwork') focusArtwork(hit.object);
  if (hit.object.userData.type === 'hunt-rabbit') collectRabbit(hit.object);
}

function bindEvents() {
  addEventListener('resize', () => {
    camera.aspect = innerWidth / innerHeight;
    camera.fov = matchMedia('(max-width: 800px)').matches ? 64 : 55;
    camera.updateProjectionMatrix();
    renderer.setSize(innerWidth, innerHeight, false);
  });
  canvas.addEventListener('pointerdown', event => {
    pointerDown = { x: event.clientX, y: event.clientY, lastX: event.clientX, lastY: event.clientY, type: event.pointerType };
    dragged = false;
    canvas.setPointerCapture(event.pointerId);
  });
  canvas.addEventListener('pointermove', event => {
    if (!pointerDown) return;
    const dx = event.clientX - pointerDown.x;
    const dy = event.clientY - pointerDown.y;
    if (selectedArtwork) {
      if (Math.abs(dx) + Math.abs(dy) > 8) dragged = true;
      return;
    }
    const movementX = event.clientX - pointerDown.lastX;
    const movementY = event.clientY - pointerDown.lastY;
    pointerDown.lastX = event.clientX;
    pointerDown.lastY = event.clientY;
    if (Math.abs(dx) + Math.abs(dy) > 5) dragged = true;
    lookYaw -= movementX * .003;
    lookPitch += movementY * .0025 * (pointerDown.type === 'touch' ? 1 : -1);
    applyLook();
  });
  canvas.addEventListener('pointerup', event => {
    if (selectedArtwork && pointerDown) {
      const dx = event.clientX - pointerDown.x;
      const dy = event.clientY - pointerDown.y;
      if ((pointerDown.type === 'touch' || matchMedia('(pointer: coarse)').matches)
        && Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.15) {
        navigateArtwork(dx < 0 ? 1 : -1);
      }
      pointerDown = null;
      return;
    }
    if (!dragged) pick(event);
    pointerDown = null;
    ui.hint.classList.add('is-hidden');
  });
  const changeView = direction => {
    if (!currentRoom) { moveToView(currentView + direction); return; }
    const pageCount = Math.max(1, Math.ceil(currentRoom.artworks.length / 7));
    if (pageCount < 2) return;
    const nextPage = (currentArtworkPage + direction + pageCount) % pageCount;
    ui.curtain.classList.add('is-active');
    setTimeout(() => {
      createRoom(currentRoom, nextPage);
      setTimeout(() => ui.curtain.classList.remove('is-active'), 180);
    }, 560);
  };
  ui.prev.addEventListener('click', () => changeView(-1));
  ui.next.addEventListener('click', () => changeView(1));
  ui.hall.addEventListener('click', () => transitionTo(null));
  ui.panelClose.addEventListener('click', () => closeArtwork());
  ui.artworkPrev.addEventListener('click', () => navigateArtwork(-1));
  ui.artworkNext.addEventListener('click', () => navigateArtwork(1));
  ui.huntProgress?.addEventListener('click', () => showHuntDialog('status'));
  ui.huntClose?.addEventListener('click', () => {
    const revealSecretDoor = ui.huntDialog.dataset.complete === 'true';
    ui.huntDialog.hidden = true;
    if (revealSecretDoor && currentRoom) transitionTo(null);
  });
  ui.huntReset?.addEventListener('click', () => {
    huntFound = [];
    saveHuntProgress();
    updateHuntUI();
    ui.huntDialog.hidden = true;
    currentRoom && !currentRoom.secret ? createRoom(currentRoom, currentArtworkPage) : createHall();
  });
  let panelSwipe = null;
  ui.panel.addEventListener('touchstart', event => {
    const touch = event.touches[0];
    panelSwipe = touch ? { x: touch.clientX, y: touch.clientY } : null;
  }, { passive: true });
  ui.panel.addEventListener('touchend', event => {
    if (!panelSwipe || !selectedArtwork) return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - panelSwipe.x;
    const dy = touch.clientY - panelSwipe.y;
    panelSwipe = null;
    if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.15) navigateArtwork(dx < 0 ? 1 : -1);
  }, { passive: true });
  ui.fullscreen.addEventListener('click', () => document.fullscreenElement ? document.exitFullscreen() : root.requestFullscreen?.());
  ui.sound?.addEventListener('click', () => {
    ambientEnabled = !ambientEnabled;
    localStorage.setItem('exhibitionAmbientSound', ambientEnabled ? 'on' : 'off');
    updateSoundControl();
    if (ambientEnabled) startAmbient(); else stopAmbient();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopAmbient();
    else if (ambientEnabled) startAmbient();
  });
  addEventListener('keydown', event => {
    if (event.key === 'Escape' && selectedArtwork) closeArtwork();
    if (event.key === 'ArrowLeft') selectedArtwork ? navigateArtwork(-1) : changeView(-1);
    if (event.key === 'ArrowRight') selectedArtwork ? navigateArtwork(1) : changeView(1);
    if (event.key.toLowerCase() === 'h') transitionTo(null);
  });
}

function render() {
  const now = performance.now();
  if (tween) {
    const t = Math.min(1, (now - tween.start) / tween.duration);
    const eased = 1 - Math.pow(1 - t, 3);
    camera.position.lerpVectors(tween.from, tween.to, eased);
    applyLook();
    if (t >= 1) tween = null;
  }
  if (roomRoot) roomRoot.position.y = Math.sin(clock.getElapsedTime() * .35) * .006;
  kineticObjects.forEach(object => { object.rotation.y += .0022; });
  if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
    scrollingTextures.forEach(texture => { texture.offset.x = (texture.offset.x - .00048 + 1) % 1; });
  }
  renderer.render(scene, camera);
}

function animate() {
  requestAnimationFrame(animate);
  render();
}

async function init() {
  updateSoundControl();
  if (!webglAvailable()) { ui.error.hidden = false; return; }
  setupRenderer();
  bindEvents();
  setProgress(15);
  try {
    const response = await fetch(document.body.dataset.api, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    exhibition = await response.json();
    if (!exhibition.rooms?.length) throw new Error('No exhibition rooms');
    try {
      const storedHunt = JSON.parse(localStorage.getItem('msieurcExhibitionHunt') || '[]');
      huntFound = Array.isArray(storedHunt) ? storedHunt.map(Number).filter(Number.isFinite) : [];
    } catch (_) { huntFound = []; }
    setProgress(20);
    await preloadExhibitionAssets();
    createHall();
    warmSceneForNavigation();
    preloadHighResolutionImages();
    setProgress(100);
    ui.loadingLabel.textContent = copy.ready;
    ui.enter.disabled = false;
    animate();
  } catch (error) {
    console.error(error);
    ui.loadingLabel.textContent = lang === 'en' ? 'The exhibition is unavailable' : 'L’exposition est indisponible';
  }
  ui.enter.addEventListener('click', async () => {
    exhibitionEntered = true;
    ui.musicCredit.hidden = false;
    startAmbient();
    try { if (!document.fullscreenElement && innerWidth < 1000) await root.requestFullscreen?.(); } catch (_) {}
    ui.hud.hidden = false;
    ui.hint.hidden = false;
    ui.rooms.hidden = false;
    updateHuntUI();
    requestAnimationFrame(() => ui.entry.classList.add('is-leaving'));
    if (exhibition?.hunt?.enabled && huntItems().length && !sessionStorage.getItem('msieurcHuntIntroSeen')) {
      sessionStorage.setItem('msieurcHuntIntroSeen', '1');
      setTimeout(() => showHuntDialog('intro'), 650);
    }
  });
  window.__EXHIBITION__ = {
    renderer, scene, camera, render,
    enter: () => ui.enter.click(),
    room: id => transitionTo(exhibition.rooms.find(item => item.id === id)),
    focusFirstArtwork: () => {
      const firstArtwork = orderedRoomArtworks()[0];
      const target = firstArtwork ? artworkObject(firstArtwork) : null;
      if (target) focusArtwork(target);
    },
    nextArtwork: () => navigateArtwork(1),
    previousArtwork: () => navigateArtwork(-1)
  };
}

init();
